/**
 * Projects kept on this device (IndexedDB, in browsers and in the desktop or mobile webview),
 * so a survey works with no network and no file system:
 *   - local projects, saved as they change;
 *   - copies of cloud projects (the server state at a seq), so they open offline; changes made
 *     offline wait in the sync queue;
 *   - their photos and plans, with a flag for the ones not yet uploaded to the cloud.
 * Every call resolves (null / [] / false) when storage is unavailable.
 */
import type { Project } from '@elec/domain';

export interface LibraryEntry {
  /** project id for local projects, "cloud:<id>" for cloud copies. */
  key: string;
  kind: 'local' | 'cloud';
  title: string;
  updatedAt: number;
  project: Project;
  /** Cloud copies: server state `project` is at `seq`. */
  cloud?: { projectId: string; teamId: string; seq: number; role: 'owner' | 'editor' | 'viewer' };
}

export interface StoredAsset {
  bytes: Uint8Array;
  mime: string;
  /** False while a cloud project's file waits to be uploaded. */
  uploaded: boolean;
}

const DB = 'tablo-library';
const PROJECTS = 'projects';
const ASSETS = 'assets';

let opening: Promise<IDBDatabase | null> | null = null;
function open(): Promise<IDBDatabase | null> {
  opening ??= new Promise((resolve) => {
    try {
      if (typeof indexedDB === 'undefined') return resolve(null);
      const req = indexedDB.open(DB, 1);
      req.onupgradeneeded = () => {
        req.result.createObjectStore(PROJECTS);
        req.result.createObjectStore(ASSETS);
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
      req.onblocked = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
  return opening;
}

/** Run one IndexedDB transaction: { value } when it committed, null when it failed. */
function run<T>(
  stores: string[],
  mode: IDBTransactionMode,
  fn: (t: IDBTransaction) => IDBRequest<T> | void,
): Promise<{ value: T | null } | null> {
  return open().then(
    (db) =>
      new Promise((resolve) => {
        if (!db) return resolve(null);
        try {
          const t = db.transaction(stores, mode);
          const req = fn(t);
          let value: T | null = null;
          if (req) req.onsuccess = () => (value = (req.result as T) ?? null);
          t.oncomplete = () => resolve({ value });
          t.onerror = () => resolve(null);
          t.onabort = () => resolve(null);
        } catch {
          resolve(null);
        }
      }),
  );
}
const tx = <T>(stores: string[], mode: IDBTransactionMode, fn: (t: IDBTransaction) => IDBRequest<T> | void) =>
  run(stores, mode, fn).then((r) => r?.value ?? null);

export const cloudKey = (projectId: string) => `cloud:${projectId}`;

export async function listLibrary(): Promise<LibraryEntry[]> {
  const all = await tx<LibraryEntry[]>([PROJECTS], 'readonly', (t) => t.objectStore(PROJECTS).getAll());
  return (all ?? []).sort((a, b) => b.updatedAt - a.updatedAt);
}

export const loadLibraryEntry = (key: string) =>
  tx<LibraryEntry>(
    [PROJECTS],
    'readonly',
    (t) => t.objectStore(PROJECTS).get(key) as IDBRequest<LibraryEntry>,
  );

/** Save (or replace) an entry; false if it could not be stored (no storage, quota). */
export const saveLibraryEntry = (e: LibraryEntry): Promise<boolean> =>
  run([PROJECTS], 'readwrite', (t) => {
    t.objectStore(PROJECTS).put(e, e.key);
  }).then((r) => r !== null);

/** Remove a project and its files from this device. */
export async function deleteLibraryEntry(key: string): Promise<void> {
  const ids = await assetKeys(key);
  await tx([PROJECTS, ASSETS], 'readwrite', (t) => {
    t.objectStore(PROJECTS).delete(key);
    for (const id of ids) t.objectStore(ASSETS).delete(`${key}/${id}`);
  });
}

async function assetKeys(key: string): Promise<string[]> {
  const range = typeof IDBKeyRange === 'undefined' ? null : IDBKeyRange.bound(`${key}/`, `${key}/\uffff`);
  if (!range) return [];
  const keys = await tx<IDBValidKey[]>([ASSETS], 'readonly', (t) => t.objectStore(ASSETS).getAllKeys(range));
  return (keys ?? []).map((k) => String(k).slice(key.length + 1));
}

export const putStoredAsset = (key: string, assetId: string, a: StoredAsset): Promise<boolean> =>
  run([ASSETS], 'readwrite', (t) => {
    t.objectStore(ASSETS).put(a, `${key}/${assetId}`);
  }).then((r) => r !== null);

/** Every file stored for a project. */
export async function storedAssets(key: string): Promise<Record<string, StoredAsset>> {
  const out: Record<string, StoredAsset> = {};
  const range = typeof IDBKeyRange === 'undefined' ? null : IDBKeyRange.bound(`${key}/`, `${key}/\uffff`);
  if (!range) return out;
  await tx([ASSETS], 'readonly', (t) => {
    const req = t.objectStore(ASSETS).openCursor(range);
    req.onsuccess = () => {
      const c = req.result;
      if (!c) return;
      out[String(c.key).slice(key.length + 1)] = c.value as StoredAsset;
      c.continue();
    };
  });
  return out;
}

/**
 * Ask the browser to keep this site's storage (not evicted when space runs low). Installed
 * web apps usually get it; returns whether storage is persistent.
 */
export async function persistStorage(): Promise<boolean> {
  try {
    const s = typeof navigator !== 'undefined' ? navigator.storage : undefined;
    if (!s?.persist) return false;
    return (await s.persisted?.()) || (await s.persist());
  } catch {
    return false;
  }
}

// ---------- wholesaler price list (one per device, for quotes) ----------

export interface StoredPriceList {
  name: string;
  importedAt: number;
  entries: Array<{ reference: string; description: string; unit: string; price: number }>;
}

let pricesDb: Promise<IDBDatabase | null> | null = null;
function openPrices(): Promise<IDBDatabase | null> {
  pricesDb ??= new Promise((resolve) => {
    try {
      if (typeof indexedDB === 'undefined') return resolve(null);
      const req = indexedDB.open('tablo-prices', 1);
      req.onupgradeneeded = () => req.result.createObjectStore('lists');
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
      req.onblocked = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
  return pricesDb;
}

function pricesTx<T>(
  mode: IDBTransactionMode,
  fn: (s: IDBObjectStore) => IDBRequest<T> | void,
): Promise<T | null> {
  return openPrices().then(
    (db) =>
      new Promise<T | null>((resolve) => {
        if (!db) return resolve(null);
        try {
          const t = db.transaction('lists', mode);
          const req = fn(t.objectStore('lists'));
          let value: T | null = null;
          if (req) req.onsuccess = () => (value = (req.result as T) ?? null);
          t.oncomplete = () => resolve(value);
          t.onerror = () => resolve(null);
        } catch {
          resolve(null);
        }
      }),
  );
}

export const loadPriceList = () =>
  pricesTx<StoredPriceList>('readonly', (s) => s.get('current') as IDBRequest<StoredPriceList>);
export const savePriceList = (l: StoredPriceList) =>
  pricesTx('readwrite', (s) => {
    s.put(l, 'current');
  }).then(() => undefined);
export const clearPriceList = () =>
  pricesTx('readwrite', (s) => {
    s.delete('current');
  }).then(() => undefined);
